import { Component, computed } from '@angular/core';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import moment from 'moment';
import { ProspectRemindersService } from '../../services/prospect-reminders.service';

/**
 * Stands in for the business name when the record has none. Deliberately conspicuous
 * rather than a graceful substitute, so the seller can tell at a glance that the prospect
 * is missing its name. Rendered as plain text, never as a link: there is no name to carry
 * into the search on the other side, so the only way out of the pop-up is `Dismiss`.
 */
const MISSING_BUSINESS_NAME = 'NO_BUSINESS_NAME';

/**
 * The prospect reminder pop-up. Mounted once by {@link ProtectedLayoutComponent}, so a
 * reminder interrupts the user on whatever page they are on.
 *
 * It is deliberately hard to ignore: no close button, `Esc` does nothing, and clicking
 * the dimmed area does nothing. There are exactly two ways out, both deliberate acts —
 * `Dismiss`, or clicking the business name. If it could be closed without acting, the
 * reminder would vanish from view while still unhandled, which is worse than never
 * having shown it.
 *
 * The dialog is only in the DOM while a reminder is showing, so it never leaves an
 * invisible mask behind that could interfere with the app's other dialogs.
 */
@Component({
  selector: 'app-prospect-reminder-dialog',
  imports: [Dialog, Button],
  templateUrl: './prospect-reminder-dialog.component.html',
})
export class ProspectReminderDialogComponent {
  constructor(private readonly reminders: ProspectRemindersService) { /**/ }

  /**
   * The reminder on screen. A getter rather than a field initializer: with ES2022 class
   * fields the initializer would run before the injected service is assigned.
   */
  get reminder() {
    return this.reminders.current;
  }


  /**
   * The prospect's business name, trimmed. Empty when the record has none, which is what
   * drops the hyperlink in favour of {@link missingBusinessName}.
   */
  readonly prospectName = computed(() => {
    const reminder = this.reminder();
    if (!reminder) return '';
    return (reminder.referred_company ?? '').trim();
  });

  /** Template-visible placeholder for a record with no business name. */
  readonly missingBusinessName = MISSING_BUSINESS_NAME;

  /**
   * `Scheduled: 08/27/2026 at 2:30 PM` — the app's US date and 12-hour time format.
   *
   * The stored values are parsed strictly as bare date and time strings and formatted
   * straight back out, with no instant in between. That is what keeps the displayed
   * time the Arizona time the user entered instead of shifting it into the viewer's own
   * timezone. The time half is omitted if there is none, though a reminder without a
   * time never reaches this screen.
   */
  readonly scheduledLabel = computed(() => {
    const reminder = this.reminder();
    if (!reminder?.reminder_date) return '';

    const day = moment(reminder.reminder_date, 'YYYY-MM-DD', true);
    const date = day.isValid() ? day.format('MM/DD/YYYY') : reminder.reminder_date;
    if (!reminder.reminder_time) return `Scheduled: ${date}`;

    const clock = moment(reminder.reminder_time, ['HH:mm:ss', 'HH:mm'], true);
    const time = clock.isValid() ? clock.format('h:mm A') : reminder.reminder_time;
    return `Scheduled: ${date} at ${time}`;
  });

  onDismiss(): void {
    this.reminders.dismissCurrent();
  }

  onOpenProspect(): void {
    this.reminders.openCurrentProspect();
  }
}
